/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable no-nested-ternary */

'use client'

import React, { ReactNode, useMemo, useRef, useState } from 'react'
import {
  Chip,
  Box,
  Stack,
  Drawer,
  type SxProps,
  Typography,
  TextField,
  Button,
} from '@mui/material'
import { Icon } from '@media-components'
import { SectionWrapper, Pagination } from '@components/common'
import {
  commonStyles,
  CustomStyles,
  getStyles,
  STYLE_SIZE_CONSTANTS,
} from '@utils/styles'
import { NotFoundCard } from '../NotFoundCard'
import defaultStyles, { StylesClassNames } from './styles'

/**
 * Scroll element into view with offset for header
 */
const scrollToElement = (
  element: HTMLElement | null,
  smooth: boolean = true
) => {
  if (!element) return

  const headerOffset = STYLE_SIZE_CONSTANTS.headerHeight + 20
  const elementPosition = element.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.scrollY - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: smooth ? 'smooth' : 'auto',
  })
}

/**
 * Internal pagination state type
 */
type PaginationState = {
  currentPage: number
  itemsPerPage: number
}

/**
 * Filter callback function type for client-side filtering
 */
type FilterCallback<T> = (data: T[], filters: { search: string }) => T[]

/**
 * Props for the PaginationSearchSection component
 * A reusable component for resource listing pages with search, filtering, and pagination
 *
 * @template T - The type of data items being rendered
 *
 * @example
 * Simple usage with internal pagination:
 * ```tsx
 * <PaginationSearchSection
 *   title="Latest Blogs"
 *   searchPlaceholder="Search blogs"
 *   data={blogData}
 *   initialItemsPerPage={10}
 *   renderItem={(item, index) => <BlogCard key={index} {...item} />}
 *   asideSections={[
 *     <CategoryFilterSection key="filter" {...filterProps} />,
 *     <NewsletterWidget key="newsletter" />,
 *   ]}
 * />
 * ```
 *
 * @example
 * With external search handler (API-based search):
 * ```tsx
 * <PaginationSearchSection
 *   title="Latest Blogs"
 *   data={searchResults}
 *   onSearch={handleApiSearch}
 *   searchValue={searchTerm}
 *   loading={isLoading}
 *   loadingSkeleton={<Skeleton />}
 *   // When using external search, you may want to disable internal pagination
 *   // and handle it externally
 *   externalPaginationProps={{
 *     totalItems: totalFromApi,
 *     totalPages: pagesFromApi,
 *     currentPage: pageFromApi,
 *     itemsPerPage: 10,
 *     onPageSelect: handlePageChange,
 *   }}
 *   renderItem={(item, index) => <BlogCard key={index} {...item} />}
 * />
 * ```
 */
export type PaginationSearchSectionProps<T> = {
  // ============================================
  // REQUIRED PROPS
  // ============================================

  /** Title displayed on the left side of the search bar */
  title: string

  /** Array of data items to be rendered */
  data: T[]

  /**
   * Render function for each data item
   * @param item - The data item to render
   * @param index - Index of the item in the array
   * @returns ReactNode to render for this item
   */
  renderItem: (item: T, index: number) => ReactNode

  // ============================================
  // SEARCH CONFIGURATION
  // ============================================

  /** Placeholder text for the search input */
  searchPlaceholder?: string

  /** Current search value (controlled - for external search) */
  searchValue?: string

  /**
   * Callback when search value changes (for API-based search)
   * If not provided, internal search state is used
   * @param value - The new search value
   */
  onSearch?: (value: string) => void

  /**
   * Client-side filter callback function
   * Use this for client-side filtering when you have all data locally
   * @param data - The full data array
   * @param filters - Object containing search string
   * @returns Filtered data array
   */
  filterCb?: FilterCallback<T>

  // ============================================
  // PAGINATION CONFIGURATION
  // ============================================

  /**
   * Initial number of items per page (default: 10)
   * The component handles pagination internally using this value
   */
  initialItemsPerPage?: number

  /**
   * Hide pagination if there's only one page of data
   */
  hidePaginationIfOnePage?: boolean

  /**
   * External pagination props - use when pagination is handled externally
   * (e.g., server-side pagination with API)
   * When provided, internal pagination is disabled
   */
  externalPaginationProps?: {
    /** Total number of items */
    totalItems: number
    /** Total number of pages */
    totalPages: number
    /** Current page number */
    currentPage: number
    /** Number of items per page */
    itemsPerPage: number
    /** Callback when page is selected */
    onPageSelect: (page: number) => void
    /** Callback when items per page changes */
    onItemsPerPageChange?: (itemsPerPage: number) => void
  }

  /** Hide pagination controls entirely */
  hidePagination?: boolean

  // ============================================
  // LOADING & EMPTY STATES
  // ============================================

  /** Whether data is currently loading */
  loading?: boolean

  /** Custom skeleton component to show while loading */
  loadingSkeleton?: ReactNode

  /** Number of skeleton items to show while loading (default: 10) */
  skeletonCount?: number

  /** Custom component to show when no data is found */
  notFoundComponent?: ReactNode

  /** Title for the default not found card */
  notFoundTitle?: string

  /** Description for the default not found card */
  notFoundDescription?: string

  /** Label for the clear search button in not found state */
  clearSearchLabel?: string

  // ============================================
  // ASIDE SECTION (RIGHT SIDE)
  // ============================================

  /**
   * Array of components to render in the aside section (right side)
   * Components are rendered in order, allowing flexible positioning
   * of filters, newsletters, ads, etc.
   *
   * @example
   * ```tsx
   * asideSections={[
   *   <CategoryFilter key="filter" />,
   *   <Newsletter key="newsletter" />,
   * ]}
   * ```
   */
  asideSections?: ReactNode[]

  /**
   * Same as asideSections but for mobile drawer
   * If not provided, asideSections will be used
   */
  mobileAsideSections?: ReactNode[]

  /**
   * Any section to render on top of main wrapper
   */
  topSections?: ReactNode[]

  // ============================================
  // MOBILE CONFIGURATION
  // ============================================

  /** Label for the mobile filter button (default: "Apply Filters") */
  mobileFilterButtonLabel?: string

  /** Title for the mobile filter drawer (default: "Apply Filters") */
  mobileDrawerTitle?: string

  // ============================================
  // MOBILE CATEGORY TAG (shown below title on mobile when a category is active)
  // ============================================

  /** The currently active category name (e.g., "AWS"). If set, shows a tag chip on mobile. */
  activeCategoryTag?: string

  /** Label prefix for the category tag (default: "Category") */
  activeCategoryLabel?: string

  onClearCategory?: () => void

  // ============================================
  // STYLING
  // ============================================

  /** Custom styles override */
  customStyles?: CustomStyles<StylesClassNames>

  /** Additional sx props for the wrapper */
  wrapperSx?: SxProps

  /** Additional components to render in the header (e.g., sort button) */
  headerActions?: ReactNode

  /** Scroll ID for the content section */
  scrollId?: string
}

/**
 * PaginationSearchSection - A reusable component for resource listing pages
 *
 * Features:
 * - Search bar with title
 * - **Internal pagination management** - just pass `initialItemsPerPage`
 * - Configurable aside section (filters, newsletter, etc.)
 * - Flexible item ordering in aside
 * - Mobile-responsive with drawer for filters
 * - Loading and empty states
 * - Fully customizable styling
 * - Optional external pagination for API-based pagination
 * - Optional client-side filtering via `filterCb`
 *
 * Use this component for:
 * - Blog listing pages
 * - Resource libraries
 * - Case studies
 * - Any paginated content with filtering
 */
export function PaginationSearchSection<T>({
  // Required
  title,
  data,
  renderItem,

  // Search
  searchPlaceholder = 'Search...',
  searchValue,
  onSearch,
  filterCb,

  // Pagination
  initialItemsPerPage = 10,
  hidePaginationIfOnePage = false,
  externalPaginationProps,
  hidePagination = false,

  // Loading & Empty
  loading = false,
  loadingSkeleton,
  skeletonCount = 10,
  notFoundComponent,
  notFoundTitle = 'No items found',
  notFoundDescription = 'Try adjusting your search or filters',
  clearSearchLabel = 'Clear Search',

  // Aside
  asideSections = [],
  mobileAsideSections,

  // Mobile
  mobileFilterButtonLabel = 'Apply Filters',
  mobileDrawerTitle = 'Categories',

  // Mobile Category Tag
  activeCategoryTag,
  activeCategoryLabel = 'Category',
  onClearCategory,

  // Styling
  customStyles,
  wrapperSx,
  headerActions,
  scrollId,

  // render any section on top of main wrapper
  topSections,
}: PaginationSearchSectionProps<T>) {
  const styles = getStyles(defaultStyles, customStyles)
  const contentRef = useRef<HTMLDivElement>(null)

  // Internal states
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [internalSearchValue, setInternalSearchValue] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: initialItemsPerPage,
  })

  // Determine if using external or internal search
  const isExternalSearch = onSearch !== undefined
  const currentSearchValue = isExternalSearch
    ? (searchValue ?? '')
    : internalSearchValue

  // Determine if using external or internal pagination
  const isExternalPagination = externalPaginationProps !== undefined

  // Apply client-side filtering if filterCb is provided
  const filteredData = useMemo(() => {
    if (filterCb && currentSearchValue) {
      return filterCb(data, { search: currentSearchValue })
    }
    return data
  }, [data, filterCb, currentSearchValue])

  // Internal pagination calculations
  const internalTotalItems = filteredData.length
  const internalTotalPages = Math.ceil(
    internalTotalItems / pagination.itemsPerPage
  )

  // Get paginated data for internal pagination mode
  const paginatedData = useMemo(() => {
    if (isExternalPagination) {
      // When using external pagination, data is already paginated
      return filteredData
    }

    // Internal pagination - slice the data
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage
    const endIndex = startIndex + pagination.itemsPerPage
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination, isExternalPagination])

  // Scroll to top of content when page changes
  const scrollToContent = (smooth: boolean = true) => {
    scrollToElement(contentRef.current, smooth)
  }

  // Handle search change
  const handleSearchChange = (value: string) => {
    if (isExternalSearch) {
      onSearch(value)
    } else {
      setInternalSearchValue(value)
    }
    // Reset to first page when searching
    if (!isExternalPagination) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }))
    }
  }

  const handleClearSearch = () => {
    handleSearchChange('')
  }

  // Internal pagination handlers
  const handlePageSelect = (page: number) => {
    setPagination((prev) => {
      const newState = { ...prev, currentPage: page }
      scrollToContent(true)
      return newState
    })
  }

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination({
      currentPage: 1, // Reset to first page
      itemsPerPage,
    })
    scrollToContent(true)
  }

  // Determine which aside sections to show in mobile
  const mobileSections = mobileAsideSections ?? asideSections

  // Determine pagination visibility
  const showPagination = useMemo(() => {
    if (hidePagination || loading) return false

    if (isExternalPagination) {
      if (hidePaginationIfOnePage && externalPaginationProps.totalPages <= 1) {
        return false
      }
      return true
    }

    // Internal pagination
    if (hidePaginationIfOnePage && internalTotalPages <= 1) {
      return false
    }
    return internalTotalItems > 0
  }, [
    hidePagination,
    loading,
    isExternalPagination,
    hidePaginationIfOnePage,
    externalPaginationProps,
    internalTotalPages,
    internalTotalItems,
  ])

  // Get pagination props (internal or external)
  const paginationProps = isExternalPagination
    ? externalPaginationProps
    : {
        totalItems: internalTotalItems,
        totalPages: internalTotalPages,
        currentPage: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
        onPageSelect: handlePageSelect,
        onItemsPerPageChange: handleItemsPerPageChange,
      }

  return (
    <SectionWrapper useAsStickyWrapper sx={wrapperSx}>
      {/* Top sections */}
      <Box
        sx={{
          display: {
            xs: 'flex',
            sm: 'none',
          },
          marginTop: '-60px',
          marginBottom: '2rem',
        }}
      >
        {(topSections?.length ?? 0) > 0 &&
          topSections?.map((section, index) => (
            <React.Fragment key={index}>{section}</React.Fragment>
          ))}
      </Box>
      {/* Mobile Filter Section */}

      <Box
        sx={{
          display: {
            md: 'none',
          },
          marginBottom: {
            xs: '1.5rem',
            sm: 'none',
          },
        }}
      >
        <Box sx={defaultStyles.headingWrapper}>
          <Typography variant="h6.600" color="text.primary">
            {title}
          </Typography>
          {mobileSections.length > 0 && (
            <Button
              startIcon={
                <Icon
                  iconName="Filter"
                  fontSize="small"
                  style={{
                    marginTop: '4px',
                  }}
                />
              }
              variant="outlined"
              onClick={() => setIsFiltersOpen(true)}
            >
              <Typography variant="h6.600" color="text.secondary">
                {mobileFilterButtonLabel}
              </Typography>
            </Button>
          )}
        </Box>

        {/* Mobile Category Tag */}
        {activeCategoryTag && (
          <Button
            variant="outlined"
            selected
            size="small"
            endIcon={
              <Icon
                iconName="Cross"
                fontSize="extraSmall"
                sx={{ cursor: 'pointer' }}
                colorValues="primary.main"
              />
            }
            sx={{
              maxHeight: '28px',
              marginTop: '0.5rem',
            }}
            onClick={onClearCategory}
          >
            <Typography variant="body3.500">
              {`${activeCategoryLabel}: ${activeCategoryTag}`}
            </Typography>
          </Button>
        )}
      </Box>

      {/* Main Content Area */}
      <Box {...styles('mainWrapper')}>
        {/* Left Content - Cards and Search */}
        <Stack {...styles('contentArea')} id={scrollId}>
          {/* Search Header */}
          <Box
            sx={{
              ...commonStyles.flexSpace(),
              position: 'sticky',
              top: `${STYLE_SIZE_CONSTANTS.headerHeight}px`,
              zIndex: 10,
              backgroundColor: 'background.paper',
              paddingBlock: '1rem',
              paddingInline: '1rem',
              marginLeft: '-1rem',
            }}
          >
            <Typography
              variant="h6.600"
              color="text.primary"
              sx={{
                display: {
                  xs: 'none',
                  md: 'block',
                },
              }}
            >
              {title}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'flex-end',
                maxWidth: {
                  xs: 'unset',
                  sm: '60%',
                },
              }}
            >
              <TextField
                value={currentSearchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                sx={{
                  flex: 1,
                  maxWidth: '100%',
                  minWidth: 'unset',
                }}
                InputProps={{
                  startAdornment: (
                    <Icon
                      iconName="AdvanceSearch"
                      fontSize="medium"
                      sx={{ marginTop: '0.5rem' }}
                    />
                  ),
                  ...(currentSearchValue.length > 0 && {
                    endAdornment: (
                      <Icon
                        iconName="Cross"
                        fontSize="medium"
                        sx={{ cursor: 'pointer' }}
                        onClick={handleClearSearch}
                      />
                    ),
                  }),
                }}
              />
              {headerActions}
            </Box>
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1 }} ref={contentRef}>
            {/* Loading State */}
            {loading ? (
              <Box {...styles('cardsWrapper')}>
                {loadingSkeleton
                  ? Array.from({ length: skeletonCount }).map((_, index) => (
                      <React.Fragment key={index}>
                        {loadingSkeleton}
                      </React.Fragment>
                    ))
                  : null}
              </Box>
            ) : paginatedData.length > 0 ? (
              /* Data Rendering */
              <Box {...styles('cardsWrapper')}>
                {paginatedData.map((item, index) => renderItem(item, index))}
              </Box>
            ) : (
              /* Empty State */
              (notFoundComponent ?? (
                <NotFoundCard
                  title={notFoundTitle}
                  desc={notFoundDescription}
                  csx={{
                    sectionWrapper: {
                      padding: '0px !important',
                      maxWidth: '450px',
                      minWidth: '100%',
                    },
                  }}
                  primaryCta={{
                    hideArrow: true,
                    label: clearSearchLabel,
                    onClick: handleClearSearch,
                  }}
                />
              ))
            )}

            {/* Pagination */}
            {showPagination && (
              <Box {...styles('pageBox')}>
                <Pagination
                  mode="client"
                  totalItems={paginationProps.totalItems}
                  totalPages={paginationProps.totalPages}
                  onPageSelect={paginationProps.onPageSelect}
                  currentPage={paginationProps.currentPage}
                  itemsPerPage={paginationProps.itemsPerPage}
                  onItemsPerPageChange={
                    paginationProps.onItemsPerPageChange ?? (() => {})
                  }
                />
              </Box>
            )}
          </Box>
        </Stack>

        {/* Aside Section (Desktop) */}
        {asideSections.length > 0 && (
          <Stack {...styles('asideContainer')}>
            <Box {...styles('asideStickyWrapper')}>
              <Stack spacing={2}>
                {asideSections.map((section, index) => (
                  <React.Fragment key={index}>{section}</React.Fragment>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        {...styles('drawer')}
      >
        {/* Sticky Header */}
        <Box {...styles('stickyHeader')}>
          <Typography variant="h6.600">{mobileDrawerTitle}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon
              iconName="Cross"
              fontSize="large"
              onClick={() => setIsFiltersOpen(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box {...styles('scrollableContent')}>
          <Stack spacing={3}>
            {mobileSections.map((section, index) => (
              <React.Fragment key={index}>{section}</React.Fragment>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </SectionWrapper>
  )
}
